/**
 * A stand-in `t` for module-scope builders called without one.
 *
 * The builders in `homeSignals`, `checklistItemMeta`, `onboardingPaths` and
 * `deadlineMilestones` take `t` as a parameter so their rendered strings can be
 * translated. Some callers legitimately want only the structural parts —
 * `minutes`, `route`, the type→bucket map — and unit tests render the panels
 * from fixtures without an i18n provider. Those need a `t` that returns the
 * English default rather than nothing.
 *
 * It must honour BOTH `t()` shapes the conventions use (`key` unquoted below:
 * the extractor scans comments, and a quoted literal here would declare a real
 * `key` entry in en/common.json — it did, once):
 *
 *   t(key, 'English')                                    → 'English'
 *   t(key, 'Hello {{name}}', { name })                   → 'Hello Ada'
 *   t(key, { count, defaultValue, defaultValue_other })  → the right plural
 *
 * A naive `(_key, fallback) => fallback` returns the OPTIONS OBJECT for the
 * third form, which React then throws on ("Objects are not valid as a React
 * child"). That is a real crash, not a test artifact — any caller relying on
 * the default would hit it.
 */

const interpolate = (template, values) =>
  typeof template === 'string' && values
    ? template.replace(/\{\{(\w+)\}\}/g, (match, key) =>
        values[key] === undefined ? match : String(values[key]),
      )
    : template;

export const identityT = (_key, fallbackOrOptions, maybeOptions) => {
  // t(key, { count, defaultValue, defaultValue_other })
  if (fallbackOrOptions && typeof fallbackOrOptions === 'object') {
    const options = fallbackOrOptions;
    const plural =
      options.count === 1 || options.defaultValue_other === undefined
        ? options.defaultValue
        : options.defaultValue_other;
    return interpolate(plural ?? '', options);
  }
  // t(key, 'English') and t(key, 'Hello {{name}}', { name })
  return interpolate(fallbackOrOptions ?? '', maybeOptions);
};

export default identityT;
